import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, MessageCircle, Check, Undo2, Pencil } from "lucide-react";
import { PlanSummary, InstStatus, eur, itDate } from "@/components/modular/PaymentParts";
import { welcomeResult } from "@/components/modular/WelcomeButton";

const err = (e) => toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Errore");
const today = () => new Date().toLocaleDateString("en-CA");

function PlanForm({ studentId, plan, onDone }) {
  const [f, setF] = useState({ plan: plan?.plan || "mensile", total: plan?.total || "", start_date: plan?.start_date || today(), count: "" });
  const save = useMutation({
    mutationFn: async () => (await api.put(`/modular-payments/${studentId}`, { ...f, total: Number(f.total), count: f.count ? Number(f.count) : null })).data,
    onSuccess: () => { toast.success("Piano di pagamento salvato"); onDone(); },
    onError: err,
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="space-y-3 border border-stone-200 rounded-xl p-4" data-testid="plan-form">
      <div className="grid grid-cols-3 gap-2">
        {[["unica", "Unica"], ["rate", "3 rate"], ["mensile", "Mensile"]].map(([v, l]) => (
          <button key={v} type="button" onClick={() => setF({ ...f, plan: v })} data-testid={`plan-type-${v}`}
            className={`rounded-lg border px-2 py-2 text-sm transition-colors ${f.plan === v ? "border-[#1C352D] bg-[#1C352D] text-white" : "border-stone-200 hover:border-stone-400"}`}>{l}</button>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div><Label className="text-xs">Totale €</Label><Input type="number" step="0.01" value={f.total} onChange={set("total")} className="mt-1" data-testid="plan-total-input"/></div>
        <div><Label className="text-xs">Prima scadenza</Label><Input type="date" value={f.start_date} onChange={set("start_date")} className="mt-1" data-testid="plan-start-input"/></div>
        <div><Label className="text-xs">N. rate (facolt.)</Label><Input type="number" min="1" value={f.count} onChange={set("count")} disabled={f.plan === "unica"} className="mt-1" data-testid="plan-count-input"/></div>
      </div>
      <p className="text-[11px] text-stone-500">Mensile: rate da € 50 ogni mese · 3 rate: ogni 3 mesi. Le rate già pagate vengono mantenute.</p>
      <div className="flex justify-end"><Button disabled={!f.total || save.isPending} onClick={() => save.mutate()} className="bg-[#1C352D] hover:bg-[#14271f]" data-testid="plan-save">Genera rate</Button></div>
    </div>
  );
}

function InstRow({ studentId, i, refresh }) {
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState({ due_date: i.due_date, amount: i.amount });
  const call = useMutation({ mutationFn: async (fn) => fn(), onSuccess: refresh, onError: err });
  const base = `/modular-payments/${studentId}/installments/${i.n}`;
  const remind = (channel) => call.mutate(async () => {
    const w = channel === "whatsapp" ? window.open("", "_blank") : null;
    try {
      const d = (await api.post(`${base}/remind`, { channel })).data;
      if (w) w.location.href = d.url; else toast.success("Sollecito email inviato");
    } catch (e) { if (w) w.close(); throw e; }
  });
  const last = (i.reminders || []).slice(-1)[0];
  return (
    <div className="py-3 flex flex-wrap items-center gap-2" data-testid={`inst-${i.n}`}>
      <div className="w-7 h-7 rounded-full bg-stone-100 grid place-items-center text-xs font-semibold shrink-0">{i.n}</div>
      {edit ? (
        <div className="flex flex-1 gap-2 items-center">
          <Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} className="h-8 w-40" data-testid={`inst-due-${i.n}`}/>
          <Input type="number" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className="h-8 w-24" data-testid={`inst-amount-${i.n}`}/>
          <Button size="sm" onClick={() => call.mutate(async () => { await api.patch(base, { due_date: f.due_date, amount: Number(f.amount) }); setEdit(false); })} data-testid={`inst-save-${i.n}`}>OK</Button>
        </div>
      ) : (
        <div className="flex-1 min-w-[150px] text-sm">
          <div className="font-medium">{eur(i.amount)} <span className="text-xs text-stone-500 font-normal">· {i.paid_at ? `pagata il ${itDate(i.paid_at)} (${i.method})` : `scade il ${itDate(i.due_date)}`}</span></div>
          {last && <div className="text-[11px] text-stone-400">{(i.reminders || []).length} sollecit{(i.reminders || []).length === 1 ? "o" : "i"} · ultimo {last.channel} il {itDate(last.at)} ({last.by})</div>}
        </div>
      )}
      <InstStatus s={i.status} testId={`inst-status-${i.n}`}/>
      {!i.paid_at ? (
        <div className="flex gap-1">
          <Button size="icon" variant="ghost" className="h-8 w-8" title="Modifica" onClick={() => setEdit(!edit)} data-testid={`inst-edit-${i.n}`}><Pencil size={14}/></Button>
          <Button size="icon" variant="ghost" className="h-8 w-8" title="Sollecito email" onClick={() => remind("email")} data-testid={`inst-remind-email-${i.n}`}><Mail size={14}/></Button>
          <Button size="icon" variant="ghost" className="h-8 w-8 text-emerald-700" title="Sollecito WhatsApp" onClick={() => remind("whatsapp")} data-testid={`inst-remind-wa-${i.n}`}><MessageCircle size={14}/></Button>
          <Button size="sm" className="h-8 bg-emerald-700 hover:bg-emerald-800" onClick={() => call.mutate(() => api.post(`${base}/pay`, { method: "bonifico" }))} data-testid={`inst-pay-${i.n}`}><Check size={13} className="mr-1"/>Pagata</Button>
        </div>
      ) : (
        <Button size="icon" variant="ghost" className="h-8 w-8" title="Annulla pagamento" onClick={() => window.confirm("Annullare il pagamento registrato?") && call.mutate(() => api.delete(`${base}/pay`))} data-testid={`inst-unpay-${i.n}`}><Undo2 size={14}/></Button>
      )}
    </div>
  );
}

export function PaymentPlanDialog({ studentId, onClose }) {
  const qc = useQueryClient();
  const [editPlan, setEditPlan] = useState(false);
  const { data } = useQuery({ queryKey: ["mod-payment", studentId], queryFn: async () => (await api.get(`/modular-payments/${studentId}`)).data });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["mod-payment", studentId] }); qc.invalidateQueries({ queryKey: ["mod-payments"] }); };
  const p = data?.plan;
  const welcome = useMutation({
    mutationFn: async () => (await api.post("/modular-payments/welcome", { student_ids: [studentId] })).data,
    onSuccess: (d) => { welcomeResult(d); refresh(); },
    onError: err,
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="payment-plan-dialog">
        <DialogHeader>
          <DialogTitle>{data ? `${data.student.last_name} ${data.student.first_name}` : "…"}</DialogTitle>
          <DialogDescription>{data?.student.phone || "nessun telefono"} · {data?.student.email || "nessuna email"}</DialogDescription>
        </DialogHeader>
        {data && (p && !editPlan ? (
          <div className="space-y-4">
            <PlanSummary plan={p}/>
            <div className="flex flex-wrap items-center gap-2 text-xs rounded-lg border border-stone-200 px-3 py-2" data-testid="plan-welcome-status">
              <span className="flex-1">{p.welcome_sent_at ? `Welcome mail inviata il ${itDate(p.welcome_sent_at)}${p.welcome_count > 1 ? ` (${p.welcome_count} volte)` : ""}${p.welcome_sent_by ? ` da ${p.welcome_sent_by}` : ""}` : "Welcome mail non ancora inviata"}</span>
              <Button size="sm" variant="outline" disabled={welcome.isPending} onClick={() => welcome.mutate()} data-testid="plan-welcome-send">{p.welcome_sent_at ? "Reinvia" : "Invia welcome mail"}</Button>
            </div>
            <div className="divide-y divide-stone-100">{p.installments.map((i) => <InstRow key={i.n} studentId={studentId} i={i} refresh={refresh}/>)}</div>
            <Button variant="outline" size="sm" onClick={() => setEditPlan(true)} data-testid="plan-edit-btn">Cambia piano</Button>
          </div>
        ) : (
          <PlanForm studentId={studentId} plan={p} onDone={() => { setEditPlan(false); refresh(); }}/>
        ))}
      </DialogContent>
    </Dialog>
  );
}
